import { useMutation } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type { ProductExportQuery } from '@/api/handlers/resources/products/requests';
import type { ErrorResponse } from '@/api/handlers/responses';
import type { ProductExportResponse } from '@/api/models';

/**
 * Reads `GET /products/export` on demand (bos-app#120): every variant row the
 * list's search, filters and branch match, across all pages.
 *
 * A read, run as a mutation because it happens only when someone presses
 * Export and its answer goes straight into a file: up to 20,000 rows have no
 * business sitting in the query cache, and nothing re-reads them.
 *
 * Unwraps the envelope (rulebook §10): a refusal rejects with the
 * `ErrorResponse`, whose `code` is `EXPORT_TOO_LARGE` when too many rows match.
 */
export const useExportProductsMutation = () => {
    const { apiClient } = useApi();

    return useMutation<
        ProductExportResponse,
        ErrorResponse,
        ProductExportQuery
    >({
        mutationFn: async (query) => {
            const result = await apiClient.products.exportProducts(query);
            if (!result.success) {
                throw result.error;
            }
            return result.response;
        },
    });
};
